import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import ReactTimeAgo from "react-time-ago";

import { useGetDetectorPipelineDetails } from "api/services/useDetector";
import { Typography } from "new-components/typography/Typography";
import { PipelineModelDetails } from "types/detectors";
import { parseDateAsUTC } from "utils/utilityFunctions";

export interface PipelineDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  detectorId: string;
}

interface PipelineRowProps {
  pipeline: PipelineModelDetails;
  defaultExpanded?: boolean;
}

function formatPipelineType(pipeline: PipelineModelDetails): string {
  if (pipeline.type === "active" && pipeline.is_edge_pipeline) {
    return "Active, Edge";
  }
  if (pipeline.type === "active") {
    return "Active";
  }
  if (pipeline.is_edge_pipeline) {
    return "Edge";
  }
  if (pipeline.type === "unclear") {
    return "Unclear Classifier";
  }
  if (pipeline.type === "oodd") {
    return "Out-of-Distribution Detector";
  }
  return "Shadow";
}

function PipelineRow({ pipeline, defaultExpanded = false }: PipelineRowProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const isTraining = !!pipeline.training_in_progress;

  return (
    <Box className="bg-white rounded-lg mb-2 overflow-hidden">
      <Box
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <Box className="flex items-center gap-3">
          <Typography variant="subtitle" className="font-semibold">
            {formatPipelineType(pipeline)}
            <span className="font-normal text-gray-600 ml-2">
              {pipeline.friendly_name || pipeline.pipeline_config}
            </span>
          </Typography>
          {isTraining && (
            <Box className="flex items-center gap-1 text-blue-600">
              <CircularProgress size={14} />
              <Typography variant="detail">Training...</Typography>
            </Box>
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box className="px-4 pb-4 pt-0">
          <Box className="flex flex-col gap-2 text-sm">
            <Box>
              <span className="text-gray-500">Pipeline ID:</span>
              <span className="ml-3 font-mono text-xs">{pipeline.id}</span>
            </Box>

            <Box>
              <span className="text-gray-500">Pipeline Config:</span>
              <span className="ml-3">{pipeline.pipeline_config || "N/A"}</span>
            </Box>

            <Box>
              <span className="text-gray-500">Latest MLBinary:</span>
              <span className="ml-3 font-mono text-xs">{pipeline.model_binary_id || "Not trained"}</span>
            </Box>

            <Box>
              <span className="text-gray-500">Friendly Name:</span>
              <span className="ml-3">{pipeline.friendly_name || "N/A"}</span>
            </Box>

            <Box>
              <span className="text-gray-500">Enabled:</span>
              <span className="ml-3">{pipeline.is_enabled ? "Yes" : "No"}</span>
            </Box>

            <Box>
              <span className="text-gray-500">Last Trained:</span>
              <span className="ml-3">
                {pipeline.trained_at ? (
                  <ReactTimeAgo date={parseDateAsUTC(pipeline.trained_at)} locale="en-US" />
                ) : (
                  "Never trained"
                )}
              </span>
            </Box>

            {isTraining && (
              <Box>
                <span className="text-gray-500">Training Started:</span>
                <span className="ml-3 text-blue-600">
                  <ReactTimeAgo date={parseDateAsUTC(pipeline.training_in_progress!.started_at)} locale="en-US" />
                </span>
              </Box>
            )}
          </Box>

          {/* Metrics and Metadata side by side */}
          <Box className="mt-4 pt-4 border-t border-gray-200 flex gap-12">
            {/* Metrics on the left */}
            <Box className="flex-1">
              <Typography variant="detail" className="font-bold text-gray-700 mb-3">Metrics</Typography>
              <Box className="flex flex-col gap-2 text-sm">
                {pipeline.metrics.balanced_accuracy !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Balanced Accuracy:</Typography>
                    <Typography variant="body">{(pipeline.metrics.balanced_accuracy * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.negative_accuracy !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">YES Accuracy:</Typography>
                    <Typography variant="body">{(pipeline.metrics.negative_accuracy * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.positive_accuracy !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">NO Accuracy:</Typography>
                    <Typography variant="body">{(pipeline.metrics.positive_accuracy * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.num_classes_measured !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500"># Classes in Eval:</Typography>
                    <Typography variant="body">{pipeline.metrics.num_classes_measured}</Typography>
                  </Box>
                )}

                {pipeline.metrics.worst_class_accuracy !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Worst Class Acc:</Typography>
                    <Typography variant="body">{(pipeline.metrics.worst_class_accuracy * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.mean_avg_precision !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">mAP:</Typography>
                    <Typography variant="body">{(pipeline.metrics.mean_avg_precision * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.precision !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Precision:</Typography>
                    <Typography variant="body">{(pipeline.metrics.precision * 100).toFixed(1)}%</Typography>
                  </Box>
                )}

                {pipeline.metrics.recall !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Recall:</Typography>
                    <Typography variant="body">{(pipeline.metrics.recall * 100).toFixed(1)}%</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Metadata on the right */}
            <Box className="flex-1">
              <Typography variant="detail" className="font-bold text-gray-700 mb-3">Metadata</Typography>
              <Box className="flex flex-col gap-2 text-sm">
                {pipeline.metrics.revision_number !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Revision:</Typography>
                    <Typography variant="body">{pipeline.metrics.revision_number}</Typography>
                  </Box>
                )}

                {pipeline.metrics.label_cnt !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Labels:</Typography>
                    <Typography variant="body">{pipeline.metrics.label_cnt}</Typography>
                  </Box>
                )}

                {pipeline.metrics.ground_truth_cnt !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Ground Truth:</Typography>
                    <Typography variant="body">{pipeline.metrics.ground_truth_cnt}</Typography>
                  </Box>
                )}

                {pipeline.metrics.training_time_s !== null && (
                  <Box className="flex gap-3">
                    <Typography variant="detail" className="text-gray-500">Training Time:</Typography>
                    <Typography variant="body">{pipeline.metrics.training_time_s.toFixed(1)}s</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export function PipelineDetailsModal({ isOpen, onClose, detectorId }: PipelineDetailsModalProps) {
  const {
    data: pipelineDetails,
    isFetching,
    refetch,
    dataUpdatedAt,
  } = useGetDetectorPipelineDetails(detectorId, {
    enabled: isOpen,
  });

  const directPipelines = (pipelineDetails?.pipelines.filter(
    (p) => p.type === "active" || p.type === "shadow"
  ) || []).sort((a, b) => {
    // Active pipeline comes first, then sort shadow by trained_at (most recent first)
    if (a.type === "active" && b.type !== "active") return -1;
    if (a.type !== "active" && b.type === "active") return 1;
    // Among shadow pipelines, sort by trained_at (most recent first)
    if (a.type === "shadow" && b.type === "shadow") {
      if (!a.trained_at && !b.trained_at) return 0;
      if (!a.trained_at) return 1;
      if (!b.trained_at) return -1;
      return new Date(b.trained_at).getTime() - new Date(a.trained_at).getTime();
    }
    return 0;
  });

  const supportingPipelines = pipelineDetails?.pipelines.filter(
    (p) => p.type === "unclear" || p.type === "oodd"
  ) || [];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "bg-[#F3F5F6]",
      }}
    >
      <DialogTitle className="flex h-[50px] justify-between items-center bg-[#E6E9ED] px-6 py-0">
        <Box className="flex items-center">
          <Typography variant="subtitle" className="text-[#0450BA]">
            Pipeline Details for {detectorId}
          </Typography>
        </Box>
        <Box className="flex items-center gap-2">
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} className="p-0">
            <HighlightOffIcon className="text-gray-600" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="p-6">
        {isFetching && !pipelineDetails ? (
          <Box className="space-y-4">
            <Skeleton height={100} />
            <Skeleton height={100} />
          </Box>
        ) : (
          <>
            {dataUpdatedAt && (
              <Box className="mb-4 text-right">
                <Typography variant="detail" className="text-gray-500">
                  Last updated: <ReactTimeAgo date={new Date(dataUpdatedAt)} locale="en-US" />
                </Typography>
              </Box>
            )}

            {/* Direct Pipelines */}
            {directPipelines.length > 0 && (
              <Box className="mb-6">
                <Typography variant="subtitle" className="mb-3 text-gray-700">
                  Direct Pipelines
                </Typography>
                {directPipelines.map((pipeline) => (
                  <PipelineRow
                    key={pipeline.id}
                    pipeline={pipeline}
                    defaultExpanded={pipeline.type === "active"}
                  />
                ))}
              </Box>
            )}

            {/* Supporting Pipelines */}
            {supportingPipelines.length > 0 && (
              <Box>
                <Typography variant="subtitle" className="mb-3 text-gray-700">
                  Supporting Pipelines
                </Typography>
                {supportingPipelines.map((pipeline) => (
                  <PipelineRow key={pipeline.id} pipeline={pipeline} />
                ))}
              </Box>
            )}

            {!pipelineDetails?.pipelines.length && (
              <Typography variant="body" className="text-center text-gray-500 py-8">
                No pipelines found for this detector.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
